import {
  RheologyDataDto,
  ThroughputControlType
} from 'providers/api';
import {
  DictionaryRange,
  HydraulicOptimiseResult,
  RecipeState
} from '../../../MineModel/types';
import { Model } from '../../../hooks/useModelState';
import PCRoute from '../../../MineModel/Route/PCRoute';
import { hydraulicRecipeFrS, hydraulicStrengthFrR } from '../../../canvas/PCFunctions/PCHydraulicFillFunctions';

// The two route fields every recipe payload carries: the node walk under the legacy `spools` key (read
// by the pour note, exports and route highlighting) and the real spool ids under `routeSpools` (what
// throughput accrues onto when the pour completes). One helper so a submit path cannot send one without
// the other.
export const routeFormFields = (route: PCRoute | undefined): { spools: string[]; routeSpools: string[] } => ({
  spools: route?.routePointStrings ?? [],
  routeSpools: route?.spoolIds ?? [],
});

export const generateSliderMarks = (min: number, max: number, step: number) => [
  ...Array(((max - min) / step) + 1),
].map((_, index) => ({
  value: min + (step * index),
  label: index === 0 || index === (max - min) / step ? min + (step * index) : undefined,
}));

export const toPercent = (value: number) => (value > 1 ? value : value * 100);

export const fromPercent = (value: number) => (value > 1 ? value / 100 : value);

export const makeDictionaryLimits = (rheologyData: RheologyDataDto): DictionaryRange => ({
  concentration: rheologyData.massConcentration,
  tons: rheologyData.tonnage,
  cement: rheologyData.binder,
  pumpPressure: {
    min: 0,
    interval: 1,
    max: rheologyData.pumpPressure.max,
    default: rheologyData.pumpPressure.default,
  },
});

export type HydraulicResults = {
  settlingVelocity: number;
  minVelocity: number;
  maxPipePressure: number;
};

export function triggerHydraulicFillStrengthFromRecipe(
  model: Model,
  binderTailingsRatio: number,
  dryConcentrationByMass: number,
  newTailingsSolidsDensity?: number,
  newBinderParticleDensity?: number,
  newCarrierFluidDensity?: number,
  tailingsDryTonnage?: number,
  wetFlowRate?: number,
  wetTonnage?: number,
): HydraulicResults | undefined {
  const dConc = fromPercent(dryConcentrationByMass);

  const output = hydraulicStrengthFrR(
    model,
    binderTailingsRatio,
    dConc,
    newTailingsSolidsDensity,
    newBinderParticleDensity,
    newCarrierFluidDensity,
    tailingsDryTonnage,
    wetFlowRate,
    wetTonnage,
  );

  model.setRecipeState(output.recipeState);
  return output.hydraulicCalculateResult as HydraulicResults | undefined;
}

export function calculateHydraulicOptimise(
  model: Model,
  binderTailingsRatio: number,
  throughputControlType: ThroughputControlType,
  throughput?: number,
): HydraulicOptimiseResult {
  hydraulicRecipeFrS(
    model,
    binderTailingsRatio,
    undefined,
    undefined,
    undefined,
    throughputControlType === ThroughputControlType.DryTonnage ? throughput : undefined,
    throughputControlType === ThroughputControlType.FlowRate ? throughput : undefined,
    throughputControlType === ThroughputControlType.WetTonnage ? throughput : undefined,
  );

  const recipeState = model.recipeState as RecipeState;
  return {
    concentrationByMass: recipeState.totalDryConcentrationByMass
      ? recipeState.totalDryConcentrationByMass * 100
      : 0,
  } as HydraulicOptimiseResult;
}
